import { Box, Button, FormField, Select, TextInput, Text } from "grommet";
import React from "react";
import { Organization, useUser } from "../account/AccountStore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import StyledModal from "../components/StyledModal";
import { useLocalStorage } from "usehooks-ts";
import AccessRuleRenderer from "../components/AccessRuleRenderer";
import { GetIdentityGroups, GetOrganization, SearchUsers } from "../general/IdentityAPI";

const IdentityRuleSelector = ({ onChange }) => {
    const user = useUser();

    const [recentPolicyTargets, setRecentPolicyTargets] = useLocalStorage("recentPolicyTargets", [] as any[]);

    const [questionnaireAccessLevels, setQuestionnaireAccessLevels] = React.useState({});
    const [accessLevels, setAccessLevels] = React.useState({});
    const [policyTarget, setPolicyTarget] = React.useState("");
    const [policyType, setPolicyType] = React.useState({ value: "membership", label: "Membership" });

    const [organization, setOrganization] = React.useState({} as Organization);

    React.useEffect(() => {
        GetOrganization(user.organization.id).then((org) => {
            setOrganization(org);
        });
    }, [user]);

    const setQuestionnaireOrganizationMembership = (membershipType) => () => {
        choosePolicyTarget(organization.id + ":" + membershipType);
    };
    const [groupSuggestions, setGroupSuggestions] = React.useState([] as any[]);
    const [userSuggestions, setUserSuggestions] = React.useState([] as any[]);

    React.useEffect(() => {
        if (policyType.value === "group") {
            GetIdentityGroups(user.organization.id).then((res) => setGroupSuggestions(res.identityGroups));
        } else if (policyType.value === "user") {
            SearchUsers(user.organization.id, policyTarget).then((users) => setUserSuggestions(users));
        } else if (policyType.value === "public") {
            setPolicyTarget("*");
        }
    }, [user, policyType]);

    React.useEffect(() => {
        if (policyType.value === "user" && policyTarget.length > 3 && policyTarget.indexOf(":") === -1) {
            SearchUsers(user.organization.id, policyTarget).then((users) => setUserSuggestions(users));
        }
    }, [policyTarget, policyType]);

    React.useEffect(() => {
        onChange(policyTarget);
    }, [policyTarget]);

    const choosePolicyTarget = (value) => {
        setPolicyTarget(value);
        setRecentPolicyTargets((prev) => {
            const newTargets = prev.filter((t) => t.value !== value);
            if (newTargets.length >= 5) {
                newTargets.pop();
            }
            return [{ value, label: value }, ...newTargets].slice(0, 10);
        });
    };

    return (
        <>
            <FormField label="Policy Type">
                <Select
                    value={policyType}
                    onChange={({ value }) => {
                        setPolicyType(value);
                    }}
                    labelKey="label"
                    valueKey="value"
                    options={[
                        { value: "membership", label: "Membership" },
                        { value: "group", label: "Group" },
                        { value: "user", label: "User" },
                        { value: "public", label: "Public" },
                    ]}
                />
            </FormField>
            {policyType.value == "group" && (
                <FormField label="Group">
                    <TextInput
                        type="text"
                        value={policyTarget}
                        onChange={(e) => {
                            setPolicyTarget(e.target.value);
                        }}
                        suggestions={groupSuggestions
                            .filter((g) => new RegExp(policyTarget, "i").test(g.name))
                            .map((g) => ({
                                label: (
                                    <Box pad="xsmall" gap={"xsmall"}>
                                        <Text>
                                            {g.name} {g.groupCustomType && <Text color="background-front">({g.groupCustomType.name})</Text>}
                                        </Text>
                                    </Box>
                                ),
                                value: g.id,
                            }))}
                        onSuggestionSelect={({ suggestion }) => choosePolicyTarget(suggestion.value + ":Group")}
                    ></TextInput>
                </FormField>
            )}
            {policyType.value == "user" && (
                <FormField label="User">
                    <TextInput
                        type="text"
                        value={policyTarget}
                        onChange={(e) => {
                            setPolicyTarget(e.target.value);
                        }}
                        suggestions={userSuggestions.map((g) => ({ label: g.displayName, value: g.id }))}
                        onSuggestionSelect={({ suggestion }) => choosePolicyTarget(suggestion.value + ":User")}
                    ></TextInput>
                </FormField>
            )}
            {policyType.value == "membership" && (
                <FormField label="Membership Type">
                    <Box direction="row" gap="small" pad="small">
                        <Button primary={policyTarget == organization.id + ":Staff"} onClick={setQuestionnaireOrganizationMembership("Staff")} label="Staff"></Button>
                        <Button primary={policyTarget == organization.id + ":Student"} onClick={setQuestionnaireOrganizationMembership("Student")} label="Students"></Button>
                        <Button primary={policyTarget == organization.id + ":Parent"} onClick={setQuestionnaireOrganizationMembership("Parent")} label="Parents"></Button>
                        <Button primary={policyTarget == organization.id + ":Guest"} onClick={setQuestionnaireOrganizationMembership("Guest")} label="Guests"></Button>
                    </Box>
                </FormField>
            )}

            {recentPolicyTargets.length > 0 && <Text>Recent Choices</Text>}
            <Box gap={"small"}>
                {recentPolicyTargets.map((target) => (
                    <Button key={target.value} label={<AccessRuleRenderer rule={target.label} />} onClick={() => choosePolicyTarget(target.value)} />
                ))}
            </Box>
        </>
    );
};

export default IdentityRuleSelector;

export const IdentityRuleSelectorSearch = ({ onChange }: { onChange: (value: string) => void }) => {
    const [showDialog, setShowDialog] = React.useState(false);

    const [policyTarget, setPolicyTarget] = React.useState("");

    return (
        <>
            <Button
                icon={<FontAwesomeIcon icon={["fad", "magnifying-glass"]} />}
                onClick={() => {
                    setShowDialog(true);
                }}
            ></Button>
            <StyledModal isOpen={showDialog} setIsOpen={() => setShowDialog(false)}>
                <Box pad="small" gap="small">
                    <IdentityRuleSelector
                        onChange={(e) => {
                            if (e) {
                                onChange(e);
                                setPolicyTarget(e);
                            }
                        }}
                    />
                    <Box direction="row" justify="between" pad="small">
                        <Button label="Close" onClick={() => setShowDialog(false)} primary />
                        <Button
                            onClick={() => {
                                setShowDialog(false);
                                onChange(policyTarget);
                            }}
                            label="Accept"
                        ></Button>
                    </Box>
                </Box>
            </StyledModal>
        </>
    );
};
